import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, GraphQLResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    BuyerPaymentsDetailRequest,
    BuyerPaymentsDetailResponse
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer/buyer-payments-list/get-payment';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { mapTranferDetailsSortColumn } from '../../helpers/sort-column-mapping';
import { _mapTransferTypeToPayerAndPaymentType } from '../../helpers/transfer-type-mapping';
import {
    EnumInput,
    SortDirection,
    TransferDetail,
    TransferDetailInput,
    TransferDetailResponse,
    TransferType
} from '../../model/models';
import { BuyerPaymentsDetailResponsePayLater } from '../pay-later/response/buyer-payment-detail-response-pay-later';

@Injectable({ providedIn: 'root' })
export class GetPaymentService extends ApiService<
    BuyerPaymentsDetailRequest,
    BuyerPaymentsDetailResponse
> {
    public requestType = BuyerPaymentsDetailRequest;
    public responseType = BuyerPaymentsDetailResponsePayLater;
    public endpointName = 'buyer/BuyerPaymentsList/GetPayment';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (isOn) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'buyer/BuyerPaymentsList/GetPayment';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: BuyerPaymentsDetailRequest
    ): { query: string } | BuyerPaymentsDetailRequest {
        if (this._isPayLaterEnabled) {
            const { skip, take, sortColumn, sortDirection } = req.pagination;
            const input: TransferDetailInput = {
                transferId: req.batchKey,
                companyFilter: {
                    supplierIds: []
                },
                invoiceNumberSearch: req.searchValue ?? '',
                suppliers: req.supplierSearchList ?? [],
                pagination: {
                    skip: skip,
                    take: take,
                    sortColumn: new EnumInput(mapTranferDetailsSortColumn(sortColumn)),
                    sortDirection: new EnumInput(SortDirection[sortDirection])
                }
            };
            const query = jsonToGraphQLQuery({
                transferDetail: {
                    __args: { input },
                    rows: {
                        isGroup: true,
                        payableId: true,
                        payableName: true,
                        invoiceDate: true,
                        invoiceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        paymentDate: true,
                        buyerPaymentAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        recipient: true,
                        supplier: true,
                        buyerFundingChargeTotal: {
                            cents: true,
                            currencyCode: true
                        },
                        buyerFundingChargePaidTotal: {
                            cents: true,
                            currencyCode: true
                        },
                        earlyPaymentDate: true,
                        onTermsPaymentDate: true,
                        transferType: true,
                        supplierKey: true,
                        numberOfDaysPaidEarly: true,
                        approvals: {
                            amount: {
                                cents: true,
                                currencyCode: true
                            },
                            invoice: {
                                date: true,
                                invoiceNumber: true
                            }
                        }
                    },
                    transferSummary: {
                        buyerPaymentAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        totalApprovedInvoiceAmount: {
                            cents: true,
                            currencyCode: true
                        },
                        transferDate: true,
                        transferType: true,
                        payerName: true,
                        recipientType: true,
                        invoiceCount: true,
                        traceNumber: true,
                        buyerAmountPaidToLsq: {
                            cents: true,
                            currencyCode: true
                        },
                        buyerAmountPaidToSupplier: {
                            cents: true,
                            currencyCode: true
                        }
                    }
                }
            });
            return { query: `{${query}}` };
        }
        return req;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }

    public responseMapper(
        res: GraphQLResponse<TransferDetailResponse> | BuyerPaymentsDetailResponsePayLater
    ): BuyerPaymentsDetailResponsePayLater {
        if (this._isPayLaterEnabled) {
            const { transferDetail } = (res as GraphQLResponse<TransferDetailResponse>).data;
            const [payer, paymentType] = _mapTransferTypeToPayerAndPaymentType(
                transferDetail.transferSummary.transferType
            );

            return ({
                buyerPaymentsSummaryItem: {
                    batchKey: transferDetail.transferSummary.id,
                    numberOfInvoices: transferDetail.transferSummary.invoiceCount,
                    payer,
                    buyerLegalName: transferDetail.transferSummary.payerName,
                    paymentAmount: transferDetail.transferSummary.buyerPaymentAmount,
                    invoiceAmount: transferDetail.transferSummary.totalApprovedInvoiceAmount,
                    paymentDate: transferDetail.transferSummary.transferDate,
                    paymentType,
                    referenceNumber: transferDetail.transferSummary.traceNumber,
                    buyerAmountPaidToLsq: transferDetail.transferSummary.buyerAmountPaidToLsq,
                    buyerAmountPaidToSupplier:
                        transferDetail.transferSummary.buyerAmountPaidToSupplier
                },
                buyerLegalName: transferDetail.transferSummary.payerName,
                rows: transferDetail.rows.map((transfer) => {
                    return {
                        isGroup: transfer.isGroup,
                        batchKey: transferDetail.transferSummary.id,
                        buyerKey: '',
                        buyerLegalName: '',
                        invoiceAmount: transfer.invoiceAmount,
                        invoiceDate: transfer.invoiceDate,
                        invoiceKey: transfer.payableId.toString(),
                        invoiceNumber: transfer.payableName,
                        numberDaysPaidEarly: transfer.numberOfDaysPaidEarly,
                        paymentAmount: transfer.buyerPaymentAmount,
                        paymentDate: transfer.paymentDate,
                        supplierKey: transfer.supplierKey,
                        supplierLegalName: transfer.supplier,
                        buyerFundingChargeTotal: transfer.buyerFundingChargeTotal,
                        buyerFundingChargePaidTotal: transfer.buyerFundingChargePaidTotal,
                        earlyPaymentDate: transfer.earlyPaymentDate,
                        payLaterDate: transfer.onTermsPaymentDate,
                        recipient: transfer.recipient,
                        paymentType: this._mapTransferDetailToPaymentType(transfer)
                    };
                }),
                totalCount: transferDetail.transferSummary.invoiceCount
            } as unknown) as BuyerPaymentsDetailResponsePayLater;
        }
        return res as BuyerPaymentsDetailResponsePayLater;
    }

    private _mapTransferDetailToPaymentType(transferDetail: TransferDetail): string {
        if (transferDetail.onTermsPaymentDate) {
            return 'PAY_LATER_PAYMENT';
        }
        if (transferDetail.earlyPaymentDate) {
            return 'EARLY_PAYMENT';
        }
        return 'INVOICE_TERM';
    }
}
